/**
 * RuntimeManagerSoundEvent - Data-driven sound system
 *
 * Data files:
 * - entity-sounds-c3data.json (auto-generated base mappings)
 * - sound-overrides.json (manual overrides, takes priority)
 *
 * Listens to game events and plays appropriate sounds.
 * The system looks up sounds for entities/weapons and plays them through CoreManagerAudio.
 * If no sound is configured, it silently does nothing (no error spam).
 */

import { audioManager } from '../core/CoreManagerAudio';
import type { Entity } from '../entities/base/Entity';

// =============================================================================
// Types
// =============================================================================

/**
 * Extended sound config for sounds with repeat/delay
 * Used in sound-overrides.json for effects like double laser
 */
interface ExtendedSoundConfig {
  /** Sound key to play */
  sound: string;
  /** Number of times to play (default: 1) */
  repeat?: number;
  /** Delay in ms between repeats (default: 0) */
  delayMs?: number;
}

/** Sound value can be a simple string or extended config */
type SoundValue = string | ExtendedSoundConfig;

interface EntitySoundsConfig {
  _meta?: { warning: string; generatedAt: string; note: string };
  entities: Record<string, Record<string, SoundValue>>;
  weapons: Record<string, Record<string, SoundValue>>;
  defaults: Record<string, SoundValue>;
}

interface SoundCatalogEntry {
  file: string;
  category: 'entity' | 'projectile' | 'ui' | 'destruction' | 'explosion';
  c3Name: string;
  c3Category: string;
}

interface SoundCatalog {
  _meta?: { warning: string; generatedAt: string; source: string };
  sounds: Record<string, SoundCatalogEntry>;
}

type SfxCategory = 'ui' | 'projectile' | 'explosion' | 'entity' | 'destruction';

export interface RuntimeManagerSoundEventConfig {
  /** If true, logs sound events to console (for debugging) */
  debug?: boolean;
}

// =============================================================================
// RuntimeManagerSoundEvent
// =============================================================================

export class RuntimeManagerSoundEvent {
  private scene: Phaser.Scene;
  private entitySounds: EntitySoundsConfig;
  private soundOverrides: EntitySoundsConfig;
  private soundCatalog: SoundCatalog;
  private loopingSounds: Map<string, { sound: Phaser.Sound.BaseSound; baseVolume: number }> = new Map();
  private debug: boolean;

  constructor(scene: Phaser.Scene, config?: RuntimeManagerSoundEventConfig) {
    this.scene = scene;
    this.debug = config?.debug ?? false;

    // Initialize with empty configs (will be populated from cache)
    this.entitySounds = { entities: {}, weapons: {}, defaults: {} };
    this.soundOverrides = { entities: {}, weapons: {}, defaults: {} };
    this.soundCatalog = { sounds: {} };

    this.loadConfigs();
    this.setupListeners();
  }

  /**
   * Load sound configuration from cache
   */
  private loadConfigs(): void {
    // Load entity-sounds-c3data.json (base, auto-generated)
    const entitySoundsData = this.scene.cache.json.get('entity-sounds-c3data');
    if (entitySoundsData) {
      this.entitySounds = entitySoundsData as EntitySoundsConfig;
      if (this.debug) {
        console.log('[RuntimeManagerSoundEvent] Loaded entity-sounds-c3data.json');
      }
    }

    // Load sound-overrides.json (manual)
    const overridesData = this.scene.cache.json.get('sound-overrides');
    if (overridesData) {
      this.soundOverrides = overridesData as EntitySoundsConfig;
      if (this.debug) {
        console.log('[RuntimeManagerSoundEvent] Loaded sound-overrides.json');
      }
    }

    // Load sounds-c3data.json (for category lookup)
    const catalogData = this.scene.cache.json.get('sounds-c3data');
    if (catalogData) {
      this.soundCatalog = catalogData as SoundCatalog;
      if (this.debug) {
        console.log(`[RuntimeManagerSoundEvent] Loaded sounds-c3data.json (${Object.keys(this.soundCatalog.sounds).length} sounds)`);
      }
    }
  }

  /**
   * Setup event listeners for game events
   */
  private setupListeners(): void {
    // Entity damaged
    this.scene.events.on('entityDamaged', (data: { entity: Entity; prefabName: string; damage: number }) => {
      this.playEntitySound(data.prefabName, 'onDamage');
    });

    // Entity died
    this.scene.events.on('entityDied', (data: { entity: Entity; prefabName: string }) => {
      this.playEntitySound(data.prefabName, 'onDeath');
    });

    // Entity abducted
    this.scene.events.on('entityAbducted', (data: { entity?: Entity; prefabName?: string; bioMatter?: number }) => {
      if (data.prefabName) {
        this.playEntitySound(data.prefabName, 'onAbducted');
      } else {
        // No prefab name, use default abduction sound
        this.playDefaultSound('onAbducted');
      }
    });

    // Weapon fired
    this.scene.events.on('weaponFired', (data: { slot: string; weapon: { id: string; name: string } }) => {
      if (data.weapon?.id) {
        this.playWeaponSound(data.weapon.id, 'onFire');
      }
    });

    // Projectile spawned (for spawn sounds)
    this.scene.events.on('projectileSpawned', (data: { projectile: { projectileType: string } }) => {
      // Projectile spawn sounds are handled by ProjectileData.spawnSound
      // This is just for logging/debug
      if (this.debug) {
        console.log(`[RuntimeManagerSoundEvent] Projectile spawned: ${data.projectile.projectileType}`);
      }
    });

    // Projectile hit
    this.scene.events.on('projectileHit', (data: { projectile: { projectileType: string }; target: unknown; damage: number }) => {
      this.playWeaponSound(data.projectile.projectileType, 'onHit');
    });

    // Tractor beam events
    this.scene.events.on('tractorBeamStarted', (data?: { weapon?: { id: string } }) => {
      const weaponId = data?.weapon?.id ?? 'punytractorbeam';
      this.playWeaponSound(weaponId, 'onStart');

      // Start loop sound
      const loopSound = this.getWeaponSound(weaponId, 'onLoop');
      if (loopSound) {
        this.startLoop('tractor', loopSound);
      }
    });

    this.scene.events.on('tractorBeamStopped', (data?: { weapon?: { id: string } }) => {
      this.stopLoop('tractor');

      const weaponId = data?.weapon?.id ?? 'punytractorbeam';
      this.playWeaponSound(weaponId, 'onStop');
    });

    // Player damage
    this.scene.events.on('playerDamaged', (data: { damage: number }) => {
      this.playEntitySound('Player', 'onDamage');
    });

    // Item collected
    this.scene.events.on('itemCollected', (data: { type: string; amount?: number }) => {
      if (data.type === 'biomatter') {
        this.playEntitySound('BiomatterPickup', 'onCollect');
      } else {
        this.playEntitySound('ItemPickup', 'onCollect');
      }
    });

    // Empty weapon (no ammo)
    this.scene.events.on('weaponEmpty', (data: { slot: string; weapon?: { id: string } }) => {
      this.playDefaultSound('onEmpty');
    });

    // Structure destroyed
    this.scene.events.on('structureDestroyed', (data: { entity: Entity; prefabName: string; level?: 'high' | 'medium' | 'low' }) => {
      // Try specific structure level first
      if (data.level) {
        const levelKey = `Structure${data.level.charAt(0).toUpperCase() + data.level.slice(1)}`;
        if (this.getEntitySound(levelKey, 'onDestroy')) {
          this.playEntitySound(levelKey, 'onDestroy');
          return;
        }
      }
      // Fall back to prefab name
      this.playEntitySound(data.prefabName, 'onDestroy');
    });

    // Boss spawned
    this.scene.events.on('bossSpawned', (data: { prefabName: string }) => {
      this.playEntitySound(data.prefabName, 'onSpawn');
    });

    // Cambios de volumen SFX (para actualizar loops activos)
    this.scene.events.on('sfxVolumeChanged', this.onSfxVolumeChanged, this);

    if (this.debug) {
      console.log('[RuntimeManagerSoundEvent] Event listeners registered');
    }
  }

  /**
   * Get sound key for an entity event
   * Priority: override > base > default
   */
  private getEntitySound(prefabName: string, event: string): string | null {
    // 1. Check overrides
    const override = this.soundOverrides.entities?.[prefabName]?.[event];
    if (override) return override;

    // 2. Check base mappings
    const base = this.entitySounds.entities?.[prefabName]?.[event];
    if (base) return base;

    // 3. Check defaults
    const defaultSound = this.soundOverrides.defaults?.[event]
                      ?? this.entitySounds.defaults?.[event];
    return defaultSound ?? null;
  }

  /**
   * Get sound config for a weapon event
   * Priority: override > base
   * Returns normalized ExtendedSoundConfig or null
   */
  private getWeaponSoundConfig(weaponType: string, event: string): ExtendedSoundConfig | null {
    const override = this.soundOverrides.weapons?.[weaponType]?.[event];
    if (override) return this.normalizeSoundValue(override);

    const base = this.entitySounds.weapons?.[weaponType]?.[event];
    return base ? this.normalizeSoundValue(base) : null;
  }

  /**
   * Legacy method for compatibility - returns just the sound key
   */
  private getWeaponSound(weaponType: string, event: string): string | null {
    const config = this.getWeaponSoundConfig(weaponType, event);
    return config?.sound ?? null;
  }

  /**
   * Normalize a SoundValue to ExtendedSoundConfig
   */
  private normalizeSoundValue(value: SoundValue): ExtendedSoundConfig {
    if (typeof value === 'string') {
      return { sound: value, repeat: 1, delayMs: 0 };
    }
    return {
      sound: value.sound,
      repeat: value.repeat ?? 1,
      delayMs: value.delayMs ?? 0,
    };
  }

  /**
   * Get category for a sound from the catalog
   */
  private getSoundCategory(soundKey: string): SfxCategory {
    const entry = this.soundCatalog.sounds?.[soundKey];
    if (entry) {
      // Map catalog category to CoreManagerAudio category
      switch (entry.category) {
        case 'entity': return 'entity';
        case 'projectile': return 'projectile';
        case 'ui': return 'ui';
        case 'destruction': return 'destruction';
        case 'explosion': return 'explosion';
        default: return 'entity';
      }
    }
    return 'entity';
  }

  /**
   * Play sound for an entity event
   */
  private playEntitySound(prefabName: string, event: string): void {
    const soundKey = this.getEntitySound(prefabName, event);
    if (soundKey) {
      this.playSfx(soundKey);
      if (this.debug) {
        console.log(`[RuntimeManagerSoundEvent] Entity "${prefabName}" ${event}: ${soundKey}`);
      }
    }
  }

  /**
   * Play sound for a weapon event
   * Supports repeat and delay for effects like double laser
   */
  private playWeaponSound(weaponType: string, event: string): void {
    const config = this.getWeaponSoundConfig(weaponType, event);
    if (!config) return;

    const { sound, repeat, delayMs } = config;

    if (this.debug) {
      console.log(`[RuntimeManagerSoundEvent] Weapon "${weaponType}" ${event}: ${sound} (repeat: ${repeat}, delay: ${delayMs}ms)`);
    }

    // Play first sound immediately
    this.playSfx(sound);

    // Play additional repeats with delay
    if (repeat && repeat > 1 && delayMs && delayMs > 0) {
      for (let i = 1; i < repeat; i++) {
        this.scene.time.delayedCall(delayMs * i, () => {
          this.playSfx(sound);
        });
      }
    }
  }

  /**
   * Play a default sound
   */
  private playDefaultSound(event: string): void {
    const soundKey = this.soundOverrides.defaults?.[event]
                  ?? this.entitySounds.defaults?.[event];
    if (soundKey) {
      this.playSfx(soundKey);
      if (this.debug) {
        console.log(`[RuntimeManagerSoundEvent] Default ${event}: ${soundKey}`);
      }
    }
  }

  /**
   * Play a sound effect through CoreManagerAudio
   */
  private playSfx(key: string): void {
    if (!this.scene.cache.audio.exists(key)) {
      // Sound not loaded, silently skip
      return;
    }

    const category = this.getSoundCategory(key);
    audioManager.playSfx(key, category, this.scene);
  }

  /**
   * Start a looping sound
   */
  private startLoop(id: string, soundKey: string): void {
    // Stop existing loop if any
    this.stopLoop(id);

    if (!this.scene.cache.audio.exists(soundKey)) {
      return;
    }

    const sfxVolume = audioManager.getSfxVolume();
    if (sfxVolume === 0) {
      return; // No iniciar loops si el SFX esta en mute
    }

    const baseVolume = 0.5;
    const sound = this.scene.sound.add(soundKey, { loop: true, volume: baseVolume * sfxVolume });
    sound.play();
    this.loopingSounds.set(id, { sound, baseVolume });

    if (this.debug) {
      console.log(`[RuntimeManagerSoundEvent] Started loop "${id}": ${soundKey}`);
    }
  }

  /**
   * Stop a looping sound
   */
  private stopLoop(id: string): void {
    const entry = this.loopingSounds.get(id);
    if (entry) {
      entry.sound.stop();
      entry.sound.destroy();
      this.loopingSounds.delete(id);

      if (this.debug) {
        console.log(`[RuntimeManagerSoundEvent] Stopped loop "${id}"`);
      }
    }
  }

  /**
   * Ajustar volumen de loops activos cuando cambia SFX
   */
  private onSfxVolumeChanged = (volume: number): void => {
    for (const entry of this.loopingSounds.values()) {
      entry.sound.setVolume(entry.baseVolume * volume);
    }
  };

  /**
   * Set debug mode
   */
  setDebug(enabled: boolean): void {
    this.debug = enabled;
    console.log(`[RuntimeManagerSoundEvent] Debug: ${enabled ? 'ON' : 'OFF'}`);
  }

  /**
   * Manually play a sound for an entity (for direct calls)
   */
  playForEntity(prefabName: string, event: string): void {
    this.playEntitySound(prefabName, event);
  }

  /**
   * Manually play a sound for a weapon (for direct calls)
   */
  playForWeapon(weaponType: string, event: string): void {
    this.playWeaponSound(weaponType, event);
  }

  /**
   * Clean up on scene shutdown
   */
  destroy(): void {
    // Prevent double destroy
    if (this.isDestroyed) return;
    this.isDestroyed = true;

    // Stop all looping sounds
    this.loopingSounds.forEach((entry, id) => {
      const sound = entry.sound;
      if (sound && sound.isPlaying) {
        sound.stop();
      }
      if (sound && !sound.pendingRemove) {
        sound.destroy();
      }
    });
    this.loopingSounds.clear();

    // Remove event listeners
    this.scene.events.off('entityDamaged');
    this.scene.events.off('entityDied');
    this.scene.events.off('entityAbducted');
    this.scene.events.off('weaponFired');
    this.scene.events.off('projectileSpawned');
    this.scene.events.off('projectileHit');
    this.scene.events.off('tractorBeamStarted');
    this.scene.events.off('tractorBeamStopped');
    this.scene.events.off('playerDamaged');
    this.scene.events.off('itemCollected');
    this.scene.events.off('weaponEmpty');
    this.scene.events.off('structureDestroyed');
    this.scene.events.off('bossSpawned');
    this.scene.events.off('sfxVolumeChanged', this.onSfxVolumeChanged, this);

    if (this.debug) {
      console.log('[RuntimeManagerSoundEvent] Destroyed');
    }
  }

  private isDestroyed = false;
}
